/* istanbul ignore file */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { fromJS } from 'immutable';
import { useReactlytics } from '@americanexpress/reactlytics';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import styles from '../styles/HotelSlider.scss';
import { HotelCard } from './HotelCard';
import { setSelectedHotels } from '../ducks';
import { MODULE_NAME } from '../utils/constants';
import { shouldRenderHotelTile } from '../utils/hotelRenderHelpers';

export const HotelSlider = ({ hotels = [], appendErrorMessage }) => {
  const intl = useIntl();
  const [index, setIndex] = useState(0);
  const { track } = useReactlytics();
  const containerRef = useRef(null);
  const slideRefs = useRef([]);
  const activeHotelIdRef = useRef(null);
  const selectedHotels = useSelector((state) => state.getIn(['modules', MODULE_NAME, 'selectedHotels'])).toJS();
  const dispatch = useDispatch();

  const SLIDE_FRACTION = 0.85;

  const visibleHotels = useMemo(
    () => hotels.filter(shouldRenderHotelTile),
    [hotels]
  );

  const setSlideRef = (el, i) => {
    slideRefs.current[i] = el;
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateLayout = () => {
      const containerW = container.clientWidth;
      const slideW = Math.min(containerW * SLIDE_FRACTION, 380);
      const pad = Math.max((containerW - slideW) / 2, 0);

      container.style.paddingInline = `${pad}px`;
      container.style.setProperty('--slide-width', `${slideW}px`);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(updateLayout);
    });

    const ro = new ResizeObserver(updateLayout);
    ro.observe(container);

    // eslint-disable-next-line consistent-return -- need to disconnect
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let rafId = 0;

    const updateActiveIndex = () => {
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;

      let bestIndex = 0;
      let bestDistance = Number.POSITIVE_INFINITY;

      slideRefs.current.forEach((el, i) => {
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const slideCenter = rect.left + rect.width / 2;
        const distance = Math.abs(slideCenter - containerCenter);

        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = i;
        }
      });

      setIndex(bestIndex);
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateActiveIndex);
    };

    updateActiveIndex();
    container.addEventListener('scroll', onScroll, { passive: true });

    const ro = new ResizeObserver(updateActiveIndex);
    ro.observe(container);

    // eslint-disable-next-line consistent-return -- remove listener
    return () => {
      cancelAnimationFrame(rafId);
      container.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [visibleHotels.length]);

  useEffect(() => {
    const activeHotel = visibleHotels[index];
    if (!activeHotel) return;

    const { hotelId, programRate } = activeHotel;
    const { programName } = programRate || {};
    const trackableProgramName = programName ? `-${programName}` : '';

    if (activeHotelIdRef.current === hotelId) return;
    activeHotelIdRef.current = hotelId;

    track({
      event: {
        identifier: 'axp - WishlistBuilder - Chat',
        element: `RecommendationTile-${hotelId}${trackableProgramName}`,
        type: 'impression',
      },
    });

    if (programName) {
      track({
        event: {
          identifier: 'axp - WishlistBuilder - Chat',
          element: `HotelProgram-${hotelId}${trackableProgramName}`,
          type: 'impression',
        },
      });
    }
  }, [index, visibleHotels, track]);

  const selectHotel = (hotel) => {
    const exists = selectedHotels?.some(
      (h) => h.hotelId === hotel.hotelId
    );

    const { programName } = hotel.programRate || {};
    const trackableProgramName = programName ? `-${programName}` : '';

    let updated;

    if (exists) {
      updated = selectedHotels.filter(
        (h) => h.hotelId !== hotel.hotelId
      );
      track({
        event: {
          identifier: 'axp - WishlistBuilder - Chat',
          element: `RemoveWishlist-${hotel.hotelId}${trackableProgramName}`,
          type: 'click',
        },
      });
    } else {
      if (selectedHotels.length === 30) {
        appendErrorMessage(
          [
            intl.formatMessage({ id: 'errorScreens.chatError.wishlist30-1' }),
            intl.formatMessage({ id: 'errorScreens.chatError.wishlist30-2' }),
          ].join('\n\n')
        );
        return;
      }
      if (selectedHotels.length === 29) {
        track({
          event: {
            identifier: 'axp - WishlistBuilder - Chat',
            element: 'Create30',
            type: 'impression',
          },
        });
      }
      track({
        event: {
          identifier: 'axp - WishlistBuilder - Chat',
          element: `WishlistAdd-${hotel.hotelId}${trackableProgramName}`,
          type: 'click',
        },
      });
      updated = [...selectedHotels, hotel];
    }

    dispatch(setSelectedHotels(fromJS(updated)));
  };

  return (
    <div className="position-relative">
      <div
        ref={containerRef}
        className={`${styles.hotelSlider} ${styles.reverseMargin}`}
        role="region"
        aria-roledescription="carousel"
        aria-label="Recommendations carousel"
      >
        {visibleHotels.map((hotel, i) => {
          const isSelected = selectedHotels?.some(
            (h) => h.hotelId === hotel.hotelId
          );

          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: 0.5, delay: 0.5 } }}
              key={hotel.hotelId ?? i}
              ref={(el) => setSlideRef(el, i)}
              data-index={i}
              className={`${styles.hotelSlide} ${
                i === index ? styles.isActive : ''
              }`}
              aria-roledescription="slide"
              aria-label={`${hotel.name} (${i + 1} of ${visibleHotels.length})`}
            >
              <HotelCard
                hotel={hotel}
                isSelected={isSelected}
                selectHotel={selectHotel}
              />
            </motion.div>
          );
        })}
      </div>

      <div className={styles.sliderCounter} aria-live="polite">
        {index + 1} of {hotels.length}
      </div>
    </div>
  );
};
HotelSlider.propTypes = {
  hotels: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    src: PropTypes.string,
    promotion: PropTypes.string,
  })).isRequired,
  appendErrorMessage: PropTypes.func.isRequired,
};
